<template>
  <div class="bg-surface-canvas dark:bg-surface-level-900 min-h-screen">
    <section
      class="bg-gradient-to-br brand-hero dark:from-primary/20 dark:to-surface-level-900 dark:via-surface-level-900 from-primary/15 overflow-hidden relative to-surface-canvas via-primary/5 w-full"
    >
      <div
        v-if="brand && brand.cover"
        class="absolute bg-center bg-cover inset-0"
        :style="{ backgroundImage: `url(${brand.cover})` }"
      />

      <div
        v-else
        class="absolute bg-[image:var(--surface-brand-cover-fallback)] dark:opacity-30 inset-0 opacity-40"
      />
    </section>

    <div class="-mt-32 lg:max-w-[1200px] lg:px-8 px-4 sm:-mt-40 sm:px-6 w-full xl:mx-auto">
      <BrandSearchCard
        ref="brandCardRef"
        :loading="brandLoad.status === 'loading'"
        :error="brandLoad.status === 'failed' ? brandLoad.message : ''"
        :brand="brand ?? {}"
        :search="search"
        @clear-search="handleClearSearch"
      />
    </div>

    <section
      v-if="brand && !search.isRedirecting.value"
      id="brand-stores"
      class="lg:max-w-[1200px] lg:px-8 mt-16 pb-20 px-4 sm:mt-20 sm:px-6 w-full xl:mx-auto"
    >
      <div class="flex items-end justify-between mb-6">
        <h2
          class="dark:text-content-inverse font-semibold sm:text-2xl text-content-level-900 text-xl"
        >
          {{ $t('commerce.brandsSlug.stores') }}
        </h2>

        <p
          v-if="displayCount > 0"
          class="dark:text-content-level-400 text-content-level-500 text-sm"
        >
          {{ $t('commerce.brandsSlug.storeCount', { count: displayCount }) }}
        </p>
      </div>

      <div
        v-if="search.isSearching.value"
        class="flex justify-center py-12"
      >
        <div class="dark:text-content-level-400 flex gap-3 items-center text-content-level-500">
          <svg
            class="animate-spin h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />

            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>

          <span class="text-sm">
            {{ $t('commerce.brandsSlug.searchingNearbyStores') }}
          </span>
        </div>
      </div>

      <template v-else>
        <div
          v-if="searchStoreCards.length > 0"
          class="gap-6 grid grid-cols-1 lg:grid-cols-3 sm:grid-cols-2"
        >
          <StoreCard
            v-for="store in searchStoreCards"
            :key="store.id"
            :store="store"
            :to="`/store/${store.slug}`"
            variant="grid"
            :show-favorite="false"
            :show-distance="store.distance_miles !== null"
            :show-c-t-a="false"
            :show-cuisine="false"
            card-radius="2xl"
          />
        </div>

        <div
          v-else-if="!search.hasSearched.value && brandStoreCards.length > 0"
          class="gap-6 grid grid-cols-1 lg:grid-cols-3 sm:grid-cols-2"
        >
          <StoreCard
            v-for="store in brandStoreCards"
            :key="store.id"
            :store="store"
            :to="`/store/${store.slug}`"
            variant="grid"
            :show-favorite="false"
            :show-distance="false"
            :show-c-t-a="false"
            :show-cuisine="false"
            card-radius="2xl"
          />
        </div>

        <div
          v-else
          class="border border-border-level-300 border-dashed dark:border-border-level-700 p-10 rounded-2xl text-center"
        >
          <p class="dark:text-content-level-400 text-content-level-600 text-sm">
            {{
              search.hasSearched.value
                ? $t('commerce.brandsSlug.noNearbyStoresMessage')
                : $t('commerce.brandsSlug.emptyBrandMessage')
            }}
          </p>

          <button
            v-if="search.hasSearched.value || search.query.value"
            type="button"
            class="bg-primary/10 cursor-pointer font-medium hover:bg-primary/20 inline-flex items-center justify-center mt-4 px-4 py-2 rounded-xl text-primary text-sm"
            @click="handleClearSearch"
          >
            {{ $t('commerce.brandsSlug.clearSearch') }}
          </button>
        </div>
      </template>
    </section>
  </div>
</template>

<script setup lang="ts">
import StoreCard from '~/components/StoreCard.vue'
import BrandSearchCard from '~/components/BrandSearchCard.vue'
import { computed, useTemplateRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useBrandStoreAddressSearch } from '~/composables/useBrandStoreAddressSearch'
import { Normalization } from '~/utils/normalization'
import { useLogger } from '~/composables/useLogger'
import { toBrandStoreCardView } from '~/utils/toBrandStoreCardView'
import { usePublicBrand } from '~/composables/usePublicBrand'

const logger = useLogger('BrandPage')

definePageMeta({
  layout: 'landing',
  sanctum: {
    excluded: true
  }
})

const route = useRoute()

const slug = computed(() => Normalization.trimmedStringValue(route.params['slug'], '') ?? '')

const { brandLoad, fetchBrand } = usePublicBrand(() => slug.value)
const brand = computed(() =>
  brandLoad.value.status === 'loaded' ? brandLoad.value.brand : undefined
)

const brandCardRef = useTemplateRef<{ clearInput: () => void }>('brandCardRef')

const search = useBrandStoreAddressSearch(slug)

const searchStoreCards = computed(() => search.searchResults.value.map(toBrandStoreCardView))
const brandStoreCards = computed(() => brand.value?.stores?.map(toBrandStoreCardView) ?? [])

const displayCount = computed(() => {
  if (search.hasSearched.value) return search.searchResults.value.length
  return brand.value?.stores_count ?? brand.value?.stores?.length ?? 0
})

const handleClearSearch = () => {
  search.clear()
  brandCardRef.value?.clearInput()
}

useSeoMeta({
  description: () =>
    brand.value?.description ?? `Browse stores from ${brand.value?.name ?? 'this brand'} on Mealz.`,
  ogDescription: () =>
    brand.value?.description ?? `Browse stores from ${brand.value?.name ?? 'this brand'} on Mealz.`,
  ogTitle: () => (brand.value === undefined ? 'Brand | Mealz' : `${brand.value.name} | Mealz`),
  ogType: 'website',
  title: () => (brand.value === undefined ? 'Brand | Mealz' : `${brand.value.name} | Mealz`),
  twitterCard: 'summary_large_image'
})

watch(slug, () => {
  handleClearSearch()
  fetchBrand().catch((fetchError) => {
    logger.error('watchSlug', fetchError)
  })
})

fetchBrand().catch((fetchError) => {
  logger.error('fetchBrand', fetchError)
})
</script>

<style scoped>
.brand-hero {
  height: clamp(280px, 40vh, 480px);
}
</style>
